import Link from "@components/Atoms/Link";
import useBreadcrumbCollapse from "@core-ui/hooks/useBreadcrumbCollapse";
import { cn } from "@core-ui/utils/cn";
import type { ArticleBreadcrumbItem } from "@ext/article/utils/getArticleBreadcrumbs";
import {
	Breadcrumb,
	BreadcrumbEllipsis,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbSeparator,
} from "@ui-kit/Breadcrumb";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@ui-kit/Dropdown";
import { TextOverflowTooltip } from "@ui-kit/Tooltip";
import { Fragment } from "react";

interface CollapsibleBreadcrumbProps {
	items: ArticleBreadcrumbItem[];
	className?: string;
	menuPortalContainer?: HTMLElement;
}

interface HiddenItemsMenuProps {
	items: ArticleBreadcrumbItem[];
	portalContainer?: HTMLElement;
}

const Separator = () => (
	<BreadcrumbSeparator>
		<span>/</span>
	</BreadcrumbSeparator>
);

const HiddenItemsMenu = ({ items, portalContainer }: HiddenItemsMenuProps) => (
	<DropdownMenu modal={false}>
		<DropdownMenuTrigger asChild>
			<button className="flex items-center text-muted hover:text-primary-fg" type="button">
				<BreadcrumbEllipsis />
			</button>
		</DropdownMenuTrigger>
		<DropdownMenuContent align="start" className="max-w-xs" portalContainer={portalContainer}>
			{items.map((item) => (
				<DropdownMenuItem asChild key={item.url}>
					<Link href={{ pathname: item.url }}>
						<TextOverflowTooltip side="right">{item.title}</TextOverflowTooltip>
					</Link>
				</DropdownMenuItem>
			))}
		</DropdownMenuContent>
	</DropdownMenu>
);

const CollapsibleBreadcrumb = ({ items, className, menuPortalContainer }: CollapsibleBreadcrumbProps) => {
	const { listRef, collapsed, isFullyCollapsed } = useBreadcrumbCollapse(
		items.length,
		items.map((item) => item.url).join("\n"),
	);
	const [first, ...rest] = items;
	const hiddenItems = rest.slice(0, collapsed);
	const visibleRest = rest.slice(collapsed);
	const itemClassName = cn("min-w-0 p-0", !isFullyCollapsed && "shrink-0");

	const renderItem = (item: ArticleBreadcrumbItem) => (
		<BreadcrumbItem className={itemClassName}>
			<BreadcrumbLink asChild>
				<Link className="min-w-0 max-w-full text-muted" href={{ pathname: item.url }}>
					<TextOverflowTooltip className="block">{item.title}</TextOverflowTooltip>
				</Link>
			</BreadcrumbLink>
		</BreadcrumbItem>
	);

	return (
		<Breadcrumb className={cn("w-full min-w-0 h-5", className)}>
			<BreadcrumbList
				className="!m-0 !p-0 !leading-normal list-none flex-nowrap overflow-hidden text-xs [&>li]:!m-0 [&>li]:!leading-normal [&>li]:h-5"
				ref={listRef}
			>
				{renderItem(first)}
				{hiddenItems.length > 0 && (
					<>
						<Separator />
						<BreadcrumbItem className="shrink-0 p-0">
							<HiddenItemsMenu items={hiddenItems} portalContainer={menuPortalContainer} />
						</BreadcrumbItem>
					</>
				)}
				{visibleRest.map((item) => (
					<Fragment key={item.url}>
						<Separator />
						{renderItem(item)}
					</Fragment>
				))}
			</BreadcrumbList>
		</Breadcrumb>
	);
};

export default CollapsibleBreadcrumb;
